<template>
    <Teleport to="body">
    <Transition
        enter-active-class="im-fade-enter-active"
        enter-from-class="im-fade-enter-from"
        leave-active-class="im-fade-leave-active"
        leave-to-class="im-fade-leave-to"
    >
        <div
            v-if="show"
            class="im-backdrop"
            @click="$emit('close')"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="modalTitleId"
        >
            <Transition
                enter-active-class="im-scale-enter-active"
                enter-from-class="im-scale-enter-from"
                leave-active-class="im-scale-leave-active"
                leave-to-class="im-scale-leave-to"
            >
                <div v-if="show" class="im-dialog" @click.stop>

                    <!-- ── HEADER ── -->
                    <div class="im-header">
                        <div class="im-header-l">
                            <div class="im-header-icon" :style="`background:rgba(${themeColors.primaryRgb},0.15);border-color:rgba(${themeColors.primaryRgb},0.3);color:${themeColors.primary};`">
                                <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
                                </svg>
                            </div>
                            <div>
                                <p class="im-header-sup">{{ t('investIn') || 'Invest In' }}</p>
                                <h3 :id="modalTitleId" class="im-header-title">{{ plan?.name }}</h3>
                            </div>
                        </div>
                        <button class="im-close" @click="$emit('close')" :aria-label="t('closeModal')">
                            <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>

                    <!-- ── SCROLLABLE BODY ── -->
                    <div class="im-body">
                        <form id="im-invest-form" @submit.prevent="handleSubmit" class="im-form">

                            <!-- Amount input -->
                            <div class="im-field">
                                <label for="investment-amount" class="im-label">{{ t('investmentAmount') }}</label>
                                <div class="im-amount-wrap">
                                    <span class="im-amount-pfx">$</span>
                                    <input
                                        id="investment-amount"
                                        v-model="form.amount"
                                        type="number"
                                        step="0.01"
                                        :min="plan?.min_amount"
                                        :max="plan?.max_amount"
                                        class="im-input"
                                        :placeholder="t('enterAmount')"
                                        :aria-describedby="amountHintId"
                                        required
                                    />
                                </div>
                                <p :id="amountHintId" class="im-hint">
                                    {{ t('min') }}: {{ currencySymbol }}{{ formatCurrency(plan?.min_amount) }}
                                    &nbsp;—&nbsp;
                                    {{ t('max') }}: {{ currencySymbol }}{{ formatCurrency(plan?.max_amount) }}
                                </p>
                                <p v-if="errors.amount" class="im-err">{{ errors.amount }}</p>
                            </div>

                            <!-- Payment Mode Selector (shown only when admin enables full-balance option) -->
                            <div v-if="investFullBalanceEnabled && form.amount" class="im-card">
                                <div class="im-card-topline" style="background:linear-gradient(90deg,transparent,rgba(99,102,241,0.4),transparent)"></div>
                                <div class="im-card-head">
                                    <div class="im-card-dot" style="background:#6366f1"></div>
                                    <h4 class="im-card-title">Payment Method</h4>
                                </div>
                                <div class="im-pm-grid">
                                    <button
                                        type="button"
                                        @click="paymentMode = 'split'"
                                        :class="paymentMode === 'split' ? 'im-pm-btn--active' : 'im-pm-btn--idle'"
                                        class="im-pm-btn"
                                    >
                                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01"/></svg>
                                        <div class="im-pm-btn-text">
                                            <span>Split Payment</span>
                                            <small>USDT + Credits</small>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        @click="paymentMode = 'full_balance'"
                                        :class="paymentMode === 'full_balance' ? 'im-pm-btn--active' : 'im-pm-btn--idle'"
                                        class="im-pm-btn"
                                    >
                                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0"><path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>
                                        <div class="im-pm-btn-text">
                                            <span>Balance Wallet</span>
                                            <small>100% USDT</small>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            <!-- Payment Breakdown -->
                            <div v-if="form.amount" class="im-card">
                                <div class="im-card-topline" style="background:linear-gradient(90deg,transparent,rgba(251,191,36,0.4),transparent)"></div>
                                <div class="im-card-head">
                                    <div class="im-card-dot" style="background:#fbbf24"></div>
                                    <h4 class="im-card-title">{{ t('paymentBreakdown') || 'Payment Breakdown' }}</h4>
                                </div>

                                <!-- Split mode breakdown -->
                                <div v-if="paymentMode === 'split'" class="im-breakdown-rows">
                                    <div class="im-brow">
                                        <div class="im-brow-l">
                                            <span class="im-brow-badge im-brow-badge--purple">USDT</span>
                                            <span class="im-brow-lbl">{{ investSplitUsdt }}% Required</span>
                                        </div>
                                        <span class="im-brow-val" :class="walletBalance >= usdtAmount ? 'im-val--green' : 'im-val--red'">
                                            {{ currencySymbol }}{{ formatCurrency(usdtAmount) }}
                                        </span>
                                    </div>
                                    <div class="im-brow">
                                        <div class="im-brow-l">
                                            <span class="im-brow-badge im-brow-badge--blue">Credits</span>
                                            <span class="im-brow-lbl">{{ investSplitCredit }}% Required</span>
                                        </div>
                                        <span class="im-brow-val" :class="creditBalance >= creditAmount ? 'im-val--green' : 'im-val--red'">
                                            {{ currencySymbol }}{{ formatCurrency(creditAmount) }}
                                        </span>
                                    </div>
                                    <div v-if="tokenShortfall > 0" class="im-brow im-brow--warn">
                                        <div class="im-brow-l">
                                            <span class="im-brow-badge im-brow-badge--yellow">Shortfall</span>
                                            <span class="im-brow-lbl">Auto-covered by USDT</span>
                                        </div>
                                        <span class="im-brow-val im-val--yellow">{{ currencySymbol }}{{ formatCurrency(tokenShortfall) }}</span>
                                    </div>
                                    <div class="im-brow im-brow--total">
                                        <span class="im-brow-total-lbl">Effective USDT Needed</span>
                                        <span class="im-brow-val im-brow-total-val" :class="walletBalance >= effectiveUsdtNeeded ? 'im-val--green' : 'im-val--red'">
                                            {{ currencySymbol }}{{ formatCurrency(effectiveUsdtNeeded) }}
                                        </span>
                                    </div>
                                    <div class="im-divider"></div>
                                    <div class="im-bal-grid">
                                        <div class="im-bal-card" :class="walletBalance >= effectiveUsdtNeeded ? 'im-bal-card--ok' : 'im-bal-card--bad'">
                                            <span class="im-bal-lbl">Your USDT</span>
                                            <span class="im-bal-val" :class="walletBalance >= effectiveUsdtNeeded ? 'im-val--green' : 'im-val--red'">
                                                {{ currencySymbol }}{{ formatCurrency(walletBalance) }}
                                            </span>
                                        </div>
                                        <div class="im-bal-card" :class="creditBalance >= creditAmount ? 'im-bal-card--ok' : 'im-bal-card--bad'">
                                            <span class="im-bal-lbl">Your Credits</span>
                                            <span class="im-bal-val" :class="creditBalance >= creditAmount ? 'im-val--green' : 'im-val--red'">
                                                {{ currencySymbol }}{{ formatCurrency(creditBalance) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-if="!hasSufficientBalances" class="im-alert im-alert--red">
                                        <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20" style="flex-shrink:0;margin-top:1px">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                                        </svg>
                                        <p>Insufficient USDT balance. Investment requires <strong>{{ currencySymbol }}{{ formatCurrency(effectiveUsdtNeeded) }} USDT</strong> total (including token shortfall coverage).</p>
                                    </div>
                                </div>

                                <!-- Full balance mode breakdown -->
                                <div v-else class="im-breakdown-rows">
                                    <div class="im-brow im-brow--total">
                                        <div class="im-brow-l">
                                            <span class="im-brow-badge im-brow-badge--purple">USDT</span>
                                            <span class="im-brow-total-lbl">Full Amount from Balance Wallet</span>
                                        </div>
                                        <span class="im-brow-val im-brow-total-val" :class="walletBalance >= fullBalanceAmount ? 'im-val--green' : 'im-val--red'">
                                            {{ currencySymbol }}{{ formatCurrency(fullBalanceAmount) }}
                                        </span>
                                    </div>
                                    <div class="im-divider"></div>
                                    <div class="im-bal-grid">
                                        <div class="im-bal-card" :class="walletBalance >= fullBalanceAmount ? 'im-bal-card--ok' : 'im-bal-card--bad'" style="grid-column:1/-1">
                                            <span class="im-bal-lbl">Your USDT Balance</span>
                                            <span class="im-bal-val" :class="walletBalance >= fullBalanceAmount ? 'im-val--green' : 'im-val--red'">
                                                {{ currencySymbol }}{{ formatCurrency(walletBalance) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-if="walletBalance < fullBalanceAmount" class="im-alert im-alert--red">
                                        <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20" style="flex-shrink:0;margin-top:1px">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                                        </svg>
                                        <p>Insufficient USDT balance. Need <strong>{{ currencySymbol }}{{ formatCurrency(fullBalanceAmount) }}</strong>, you have <strong>{{ currencySymbol }}{{ formatCurrency(walletBalance) }}</strong>.</p>
                                    </div>
                                </div>
                            </div>

                            <!-- General error -->
                            <div v-if="errors.general" class="im-alert im-alert--red">
                                <svg width="13" height="13" fill="currentColor" viewBox="0 0 20 20" style="flex-shrink:0;margin-top:1px">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                                </svg>
                                <p>{{ errors.general }}</p>
                            </div>

                            <!-- Invalid amount note -->
                            <p v-if="!isValidAmount && form.amount" class="im-invalid-note">{{ t('invalidAmountRange') }}</p>

                        </form>
                    </div>

                    <!-- ── STICKY FOOTER — always visible, never overlaps ── -->
                    <div class="im-footer">
                        <button
                            type="submit"
                            form="im-invest-form"
                            :disabled="localProcessing || !isValidAmount"
                            class="im-submit"
                            :style="localProcessing || !isValidAmount
                                ? 'background:#1e293b;color:#4b5563;cursor:not-allowed;opacity:0.55'
                                : `background:linear-gradient(135deg,#7c3aed,${themeColors.primary});box-shadow:0 4px 20px rgba(${themeColors.primaryRgb},0.38);color:#fff;`"
                        >
                            <template v-if="localProcessing">
                                <svg class="im-spin" width="16" height="16" fill="none" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:0.25"/>
                                    <path fill="currentColor" style="opacity:0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                </svg>
                                {{ t('processing') }}...
                            </template>
                            <template v-else>
                                <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
                                </svg>
                                {{ t('confirmInvestment') }}
                            </template>
                        </button>
                    </div>

                </div>
            </Transition>
        </div>
    </Transition>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'
import { useToast } from '@/composables/useToast'

const props = defineProps({
    show: { type: Boolean, default: false },
    plan: { type: Object, default: null },
    form: { type: Object, required: true },
    processing: { type: Boolean, default: false },
    walletBalance: { type: Number, default: 0 },
    creditBalance: { type: Number, default: 0 },
    investSplitUsdt: { type: Number, default: 50 },
    investSplitCredit: { type: Number, default: 50 },
    investFullBalanceEnabled: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'submit'])

const page = usePage()
const { t } = useTranslation()
const { success, error } = useToast()

const localProcessing = ref(false)
const errors = ref({ amount: '', general: '' })
const paymentMode = ref('split')

const primaryColor   = computed(() => page.props.primaryColor || '#86efac')
const currencySymbol = computed(() => page.props.currencySymbol || '$')

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return { r: 134, g: 239, b: 172 }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 134, g: 239, b: 172 }
}
const rgbToHex = (r, g, b) => '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
const adjustColor = (hex, amt) => {
    const { r, g, b } = hexToRgb(hex)
    return rgbToHex(Math.min(255,Math.max(0,r+amt)), Math.min(255,Math.max(0,g+amt)), Math.min(255,Math.max(0,b+amt)))
}

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}))

const modalTitleId = computed(() => `im-title-${props.plan?.id || 'x'}`)
const amountHintId = computed(() => `im-hint-${props.plan?.id || 'x'}`)

const isValidAmount = computed(() => {
    if (!props.form.amount || !props.plan) return false
    const amt = parseFloat(props.form.amount)
    return amt >= props.plan.min_amount && amt <= props.plan.max_amount
})

const round = (v) => Math.round(v * 1e8) / 1e8

const usdtAmount = computed(() => round((parseFloat(props.form.amount) || 0) * props.investSplitUsdt / 100))
const creditAmount = computed(() => round((parseFloat(props.form.amount) || 0) * props.investSplitCredit / 100))
const tokenShortfall = computed(() => round(Math.max(0, creditAmount.value - (parseFloat(props.creditBalance) || 0))))
const effectiveUsdtNeeded = computed(() => round(usdtAmount.value + tokenShortfall.value))
const hasSufficientBalances = computed(() => (parseFloat(props.walletBalance) || 0) >= effectiveUsdtNeeded.value)
const fullBalanceAmount = computed(() => round(parseFloat(props.form.amount) || 0))
const isFullBalanceMode = computed(() => props.investFullBalanceEnabled && paymentMode.value === 'full_balance')

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(v) || 0)

const calculateDailyReturn = () => {
    if (!props.plan || !props.form.amount) return '0.00'
    return formatCurrency((parseFloat(props.form.amount) * props.plan.interest_rate) / 100)
}

const calculateTotalReturn = () => {
    if (!props.plan || !props.form.amount) return '0.00'
    const amt = parseFloat(props.form.amount)
    if (props.plan.duration_type === 'lifetime') return formatCurrency(amt * (parseFloat(props.plan.profit_multiplier || 0)))
    return formatCurrency((amt * props.plan.interest_rate / 100) * props.plan.duration_days)
}

const validateForm = () => {
    errors.value = { amount: '', general: '' }
    if (!props.form.amount) { errors.value.amount = t('amountRequired'); return false }
    if (!isValidAmount.value) { errors.value.amount = t('invalidAmountRange'); return false }
    return true
}

const handleSubmit = () => {
    if (!validateForm()) return
    localProcessing.value = true
    errors.value = { amount: '', general: '' }
    router.post('/user/invest', { plan_id: props.plan.id, amount: props.form.amount, payment_mode: isFullBalanceMode.value ? 'full_balance' : 'split' }, {
        preserveScroll: true,
        onSuccess: () => { localProcessing.value = false; success(t('investmentSuccessful')); emit('close') },
        onError: (errs) => {
            localProcessing.value = false
            if (errs.amount)   { errors.value.amount  = errs.amount;   error(errs.amount) }
            if (errs.plan_id)  { errors.value.general = errs.plan_id;  error(errs.plan_id) }
            if (errs.error)    { errors.value.general = errs.error;    error(errs.error) }
            if (errs.message)  { errors.value.general = errs.message;  error(errs.message) }
            if (!errors.value.amount && !errors.value.general) { errors.value.general = t('investmentFailed'); error(t('investmentFailed')) }
        },
        onFinish: () => { localProcessing.value = false }
    })
}

watch(() => props.show, (v) => {
    if (!v) {
        errors.value = { amount: '', general: '' }
        paymentMode.value = 'split'
    }
})
</script>

<style scoped>
/* ── Backdrop transitions ── */
.im-fade-enter-active, .im-fade-leave-active { transition: opacity 0.28s ease; }
.im-fade-enter-from, .im-fade-leave-to { opacity: 0; }

/* ── Dialog transitions ── */
.im-scale-enter-active, .im-scale-leave-active { transition: opacity 0.28s ease, transform 0.28s cubic-bezier(0.22,1,0.36,1); }
.im-scale-enter-from, .im-scale-leave-to { opacity: 0; transform: scale(0.94) translateY(10px); }

/* ── Backdrop ── */
.im-backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.72);
    backdrop-filter: blur(6px);
    z-index: 99999;
    display: flex; align-items: flex-start; justify-content: center;
    padding: 16px 12px 16px;
    padding-top: max(16px, env(safe-area-inset-top));
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
}

/* ── Dialog ── */
.im-dialog {
    background: rgba(10,4,22,0.98);
    border: 1px solid rgba(167,93,237,0.2);
    border-radius: 20px;
    width: 100%;
    max-width: 460px;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(167,93,237,0.08);
    position: relative;
    flex-shrink: 0;
}
/* top accent line */
.im-dialog::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.55), rgba(232,121,249,0.3), transparent);
}

/* ── Header ── */
.im-header {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 16px 18px;
    border-bottom: 1px solid rgba(167,93,237,0.12);
    flex-shrink: 0;
}
.im-header-l { display: flex; align-items: center; gap: 11px; min-width: 0; }
.im-header-icon {
    width: 38px; height: 38px; border-radius: 10px; border: 1px solid;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.im-header-sup   { font-size: 9px; font-weight: 700; color: rgba(167,93,237,0.7); text-transform: uppercase; letter-spacing: 0.12em; margin: 0 0 2px; }
.im-header-title { font-size: 15px; font-weight: 900; color: #fff; margin: 0; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.im-close {
    width: 32px; height: 32px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.04); color: rgba(148,163,184,0.7);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    cursor: pointer; transition: all 0.2s;
}
.im-close:hover { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.3); color: #f87171; }

/* ── Scrollable body ── */
.im-body { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 16px 18px 20px; -webkit-overflow-scrolling: touch; }
.im-body::-webkit-scrollbar { width: 4px; }
.im-body::-webkit-scrollbar-track { background: transparent; }
.im-body::-webkit-scrollbar-thumb { background: rgba(167,93,237,0.25); border-radius: 2px; }

/* ── Form ── */
.im-form { display: flex; flex-direction: column; gap: 14px; }

/* ── Amount field ── */
.im-field   { display: flex; flex-direction: column; gap: 6px; }
.im-label   { font-size: 12px; font-weight: 700; color: rgba(226,232,240,0.85); }
.im-amount-wrap { position: relative; display: flex; align-items: center; }
.im-amount-pfx  { position: absolute; left: 13px; font-size: 14px; font-weight: 800; color: rgba(148,163,184,0.5); pointer-events: none; }
.im-input {
    width: 100%; padding: 12px 14px 12px 28px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(167,93,237,0.2);
    border-radius: 11px; font-size: 15px; font-weight: 700; color: #fff; outline: none;
    transition: border-color 0.2s, background 0.2s;
}
.im-input:focus { border-color: rgba(167,93,237,0.5); background: rgba(167,93,237,0.05); }
.im-input::placeholder { color: rgba(148,163,184,0.3); font-weight: 500; }
.im-hint { font-size: 11px; font-weight: 600; color: rgba(148,163,184,0.5); margin: 0; }
.im-err  { font-size: 11px; font-weight: 600; color: #f87171; margin: 0; }

/* ── Card ── */
.im-card {
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 13px; padding: 14px;
    position: relative; overflow: hidden;
}
.im-card-topline {
    position: absolute; top: 0; left: 8%; right: 8%; height: 1px;
    pointer-events: none;
}
.im-card-head  { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.im-card-dot   { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.im-card-title { font-size: 12px; font-weight: 800; color: rgba(226,232,240,0.9); text-transform: uppercase; letter-spacing: 0.06em; margin: 0; }

/* ── Summary rows ── */
.im-summary-rows { display: flex; flex-direction: column; gap: 9px; }
.im-row {
    display: flex; align-items: baseline; justify-content: space-between;
    gap: 8px; min-width: 0;
}
.im-row-lbl       { font-size: 12px; font-weight: 600; color: rgba(148,163,184,0.6); flex: 1; min-width: 0; }
.im-row-val       { font-size: 13px; font-weight: 800; flex-shrink: 0; }
.im-row-val--white { color: #fff; }
.im-row-muted     { font-size: 10.5px; font-weight: 600; color: rgba(148,163,184,0.5); }

/* ── Breakdown rows ── */
.im-breakdown-rows { display: flex; flex-direction: column; gap: 8px; }

.im-brow {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; padding: 8px 10px; border-radius: 8px;
    background: rgba(255,255,255,0.03);
}
.im-brow--warn  { background: rgba(251,191,36,0.05); border: 1px solid rgba(251,191,36,0.14); }
.im-brow--total { background: rgba(167,93,237,0.06); border: 1px solid rgba(167,93,237,0.16); padding: 10px 12px; }

.im-brow-l    { display: flex; align-items: center; gap: 7px; min-width: 0; }
.im-brow-lbl  { font-size: 12px; font-weight: 600; color: rgba(148,163,184,0.65); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.im-brow-val  { font-size: 13px; font-weight: 800; flex-shrink: 0; white-space: nowrap; }

.im-brow-total-lbl { font-size: 12px; font-weight: 700; color: rgba(226,232,240,0.85); flex: 1; min-width: 0; }
.im-brow-total-val { font-size: 14px; font-weight: 900; }

/* Badges */
.im-brow-badge {
    font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.07em;
    padding: 2px 7px; border-radius: 5px; flex-shrink: 0;
}
.im-brow-badge--purple { background: rgba(167,93,237,0.18); color: #c084fc; }
.im-brow-badge--blue   { background: rgba(96,165,250,0.18);  color: #93c5fd; }
.im-brow-badge--yellow { background: rgba(251,191,36,0.18);  color: #fde68a; }

/* Value colours */
.im-val--green  { color: #4ade80; }
.im-val--red    { color: #f87171; }
.im-val--yellow { color: #fbbf24; }

/* Divider */
.im-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 4px 0; }

/* Balance grid */
.im-bal-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}
.im-bal-card {
    padding: 10px 12px; border-radius: 9px; border: 1px solid;
    display: flex; flex-direction: column; gap: 4px;
}
.im-bal-card--ok  { background: rgba(74,222,128,0.05);  border-color: rgba(74,222,128,0.15); }
.im-bal-card--bad { background: rgba(248,113,113,0.05); border-color: rgba(248,113,113,0.15); }
.im-bal-lbl { font-size: 10px; font-weight: 700; color: rgba(148,163,184,0.55); text-transform: uppercase; letter-spacing: 0.07em; }
.im-bal-val { font-size: 14px; font-weight: 900; }

/* ── Alert ── */
.im-alert {
    display: flex; align-items: flex-start; gap: 9px;
    padding: 11px 13px; border-radius: 10px; border: 1px solid;
    font-size: 12px; font-weight: 600; line-height: 1.5;
}
.im-alert--red { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.25); color: #fca5a5; }

/* Invalid note */
.im-invalid-note { font-size: 11.5px; font-weight: 600; color: #f87171; text-align: center; margin: 0; }

/* ── Payment mode selector ── */
.im-pm-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 7px;
}
.im-pm-btn {
    display: flex; flex-direction: row; align-items: center; gap: 7px;
    padding: 8px 10px; border-radius: 9px; border: 1.5px solid;
    font-size: 11px; font-weight: 700; cursor: pointer; transition: all 0.18s ease;
    background: transparent; text-align: left; width: 100%;
}
.im-pm-btn-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.im-pm-btn span  { font-size: 11px; font-weight: 700; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.im-pm-btn small { font-size: 9.5px; font-weight: 500; opacity: 0.6; white-space: nowrap; }
.im-pm-btn--idle  { border-color: rgba(255,255,255,0.1); color: rgba(148,163,184,0.8); }
.im-pm-btn--idle:hover { border-color: rgba(99,102,241,0.4); color: #a5b4fc; }
.im-pm-btn--active { border-color: rgba(99,102,241,0.6); background: rgba(99,102,241,0.12); color: #a5b4fc; }

/* ── Sticky footer ── */
.im-footer {
    flex-shrink: 0;
    padding: 12px 18px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(167,93,237,0.1);
    background: rgba(10,4,22,0.98);
}

/* ── Submit button ── */
.im-submit {
    width: 100%; padding: 14px 20px;
    border-radius: 12px; border: none;
    font-size: 14px; font-weight: 800; letter-spacing: 0.01em;
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    cursor: pointer; transition: transform 0.2s, filter 0.2s;
}
.im-submit:not([disabled]):hover { transform: translateY(-2px); filter: brightness(1.1); }
.im-spin { animation: imSpin 0.8s linear infinite; }
@keyframes imSpin { to { transform: rotate(360deg); } }

/* ── Mobile: tablets & large phones ── */
@media (max-width: 520px) {
    .im-backdrop { padding: 8px; padding-top: max(8px, env(safe-area-inset-top)); }
    .im-dialog   { max-height: calc(100vh - 16px); max-height: calc(100dvh - 16px); border-radius: 18px; }
    .im-header   { padding: 14px 16px; }
    .im-body     { padding: 14px 14px 16px; }
    .im-footer   { padding: 10px 14px; padding-bottom: max(10px, env(safe-area-inset-bottom)); }
    .im-card     { padding: 12px; }
    .im-submit   { padding: 13px 18px; font-size: 13.5px; }
}

/* ── Mobile: standard phones (≤ 420px) ── */
@media (max-width: 420px) {
    .im-backdrop { padding: 6px; padding-top: max(6px, env(safe-area-inset-top)); }
    .im-dialog   { border-radius: 16px; }
    .im-header   { padding: 13px; gap: 10px; }
    .im-body     { padding: 12px 12px 14px; }
    .im-footer   { padding: 10px 12px; padding-bottom: max(10px, env(safe-area-inset-bottom)); }
    .im-form     { gap: 12px; }
    .im-card     { padding: 11px 10px; }
    .im-brow     { padding: 7px 9px; gap: 8px; }
    .im-brow-lbl { font-size: 11px; }
    .im-brow-val { font-size: 12px; }
    .im-brow-total-lbl { font-size: 11.5px; }
    .im-brow-total-val { font-size: 13px; }
    .im-bal-val  { font-size: 13px; }
    .im-submit   { padding: 12px 14px; font-size: 13px; }
    .im-pm-btn   { padding: 7px 9px; gap: 6px; }
}

/* ── Mobile: small phones (≤ 380px) ── */
@media (max-width: 380px) {
    .im-backdrop { padding: 4px; padding-top: max(4px, env(safe-area-inset-top)); }
    .im-dialog   { border-radius: 14px; }
    .im-header   { padding: 11px; }
    .im-header-icon { width: 32px; height: 32px; }
    .im-header-title { font-size: 13px; }
    .im-body     { padding: 10px 10px 12px; }
    .im-footer   { padding: 9px 10px; padding-bottom: max(9px, env(safe-area-inset-bottom)); }
    .im-form     { gap: 10px; }
    .im-card     { padding: 10px 9px; }
    .im-pm-grid  { grid-template-columns: 1fr 1fr; gap: 5px; }
    .im-pm-btn   { padding: 7px 8px; gap: 5px; }
    .im-brow     { padding: 6px 8px; gap: 6px; }
    .im-brow-badge { font-size: 8px; padding: 2px 5px; }
    .im-brow-lbl { font-size: 10.5px; }
    .im-brow-val { font-size: 11.5px; }
    .im-brow-total-lbl { font-size: 11px; }
    .im-brow-total-val { font-size: 12.5px; }
    .im-bal-grid { grid-template-columns: 1fr 1fr; gap: 5px; }
    .im-bal-val  { font-size: 12.5px; }
    .im-row-lbl  { font-size: 11px; }
    .im-row-val  { font-size: 12px; }
    .im-submit   { padding: 11px 12px; font-size: 12.5px; }
    .im-card-title { font-size: 11px; }
    .im-hint, .im-err { font-size: 10.5px; }
}

/* ── Mobile: extra small phones (≤ 340px) ── */
@media (max-width: 340px) {
    .im-dialog   { border-radius: 12px; }
    .im-header   { padding: 10px; }
    .im-header-icon { width: 28px; height: 28px; border-radius: 8px; }
    .im-body     { padding: 8px 8px 10px; }
    .im-footer   { padding: 8px; padding-bottom: max(8px, env(safe-area-inset-bottom)); }
    .im-card     { padding: 9px 8px; }
    .im-brow     { padding: 6px 7px; }
    .im-input    { padding: 10px 12px 10px 26px; font-size: 14px; }
    .im-submit   { padding: 10px 12px; font-size: 12px; }
}
</style>
